import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button, Portal, tokens } from "@fluentui/react-components";
import { Dismiss20Regular, Flag20Regular, HandPoint20Regular } from "@fluentui/react-icons";
import { helpText } from "./strings";
import {
  anchorNameOf,
  localizedSection,
  localizedStep,
  type HelpLanguage,
  type HelpStep,
  type PageHelp,
} from "./types";
import { HELP_PANEL_ATTR, stepAtElement } from "./dom";
import { cardPosition } from "./position";
import { EnPill } from "./HelpTour";
import { Z } from "./ui";

interface Props {
  page: PageHelp;
  /** Already filtered by permission. */
  steps: HelpStep[];
  language: HelpLanguage;
  /** "Continue from here": hand the step to the tour. */
  onContinue: (step: HelpStep) => void;
  onClose: () => void;
}

interface Hit {
  step: HelpStep;
  element: HTMLElement;
}

const CARD_WIDTH = 340;
const MISS_MS = 2200;
/** Every event of a press sequence is swallowed: a select opens on mousedown, a menu item commits on mouseup. */
const SWALLOWED = ["pointerdown", "pointerup", "mousedown", "mouseup", "dblclick", "contextmenu"] as const;

/**
 * "Point at a field": the page becomes inert, an outline follows the pointer,
 * a click shows the entry documenting that control. Asking what a button does
 * must never be the same as pressing it.
 */
export function HelpInspect({ page, steps, language, onContinue, onClose }: Props) {
  const t = helpText(language);
  const index = useMemo(() => {
    const map = new Map<string, HelpStep>();
    for (const step of steps) {
      const name = anchorNameOf(step.selector);
      if (name && !map.has(name)) map.set(name, step);
    }
    return map;
  }, [steps]);

  const [hover, setHover] = useState<Hit | null>(null);
  const [pinned, setPinned] = useState<Hit | null>(null);
  const [miss, setMiss] = useState<{ x: number; y: number } | null>(null);
  const [, setTick] = useState(0);
  const cache = useRef<{ target: EventTarget | null; hit: Hit | null }>({ target: null, hit: null });
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState(200);

  useEffect(() => {
    const inPanel = (e: Event) => e.target instanceof Element && !!e.target.closest(`[${HELP_PANEL_ATTR}]`);
    const resolve = (target: EventTarget | null): Hit | null => {
      if (cache.current.target === target) return cache.current.hit;
      const hit = stepAtElement(target instanceof Element ? target : null, index);
      cache.current = { target, hit };
      return hit;
    };
    const onMove = (e: PointerEvent) => {
      if (inPanel(e)) return;
      const hit = resolve(e.target);
      setHover((prev) => (prev?.element === hit?.element ? prev : hit));
    };
    const onClick = (e: MouseEvent) => {
      if (inPanel(e)) return;
      e.preventDefault();
      e.stopPropagation();
      const hit = resolve(e.target);
      if (hit) {
        setPinned(hit);
        setMiss(null);
      } else {
        setPinned(null);
        setMiss({ x: e.clientX, y: e.clientY });
      }
    };
    const swallow = (e: Event) => {
      if (inPanel(e)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    const onScroll = () => setTick((n) => n + 1);

    window.addEventListener("pointermove", onMove, true);
    window.addEventListener("click", onClick, true);
    SWALLOWED.forEach((type) => window.addEventListener(type, swallow, true));
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);

    // the mode announces itself with the help cursor everywhere outside the guide's own panels
    const style = document.createElement("style");
    style.textContent = `body *:not([${HELP_PANEL_ATTR}]):not([${HELP_PANEL_ATTR}] *) { cursor: help !important; }`;
    document.head.appendChild(style);

    return () => {
      window.removeEventListener("pointermove", onMove, true);
      window.removeEventListener("click", onClick, true);
      SWALLOWED.forEach((type) => window.removeEventListener(type, swallow, true));
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      style.remove();
    };
  }, [index, onClose]);

  useEffect(() => {
    if (!miss) return;
    const timer = window.setTimeout(() => setMiss(null), MISS_MS);
    return () => window.clearTimeout(timer);
  }, [miss]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardHeight(cardRef.current.offsetHeight);
  }, [pinned, language]);

  const highlight = (pinned ?? hover)?.element.getBoundingClientRect();
  const bannerTitle = (hover ?? pinned) ? localizedStep((hover ?? pinned)!.step, language).value.title : null;
  const pinnedText = pinned ? localizedStep(pinned.step, language) : null;
  const pinnedRect = pinned?.element.getBoundingClientRect();
  const pos = pinnedRect
    ? cardPosition(
        { top: pinnedRect.top - 4, bottom: pinnedRect.bottom + 4, left: pinnedRect.left, width: pinnedRect.width },
        { width: CARD_WIDTH, height: cardHeight }
      )
    : null;

  return (
    <Portal>
      <div {...{ [HELP_PANEL_ATTR]: "inspect" }}>
        {highlight && (
          <div
            aria-hidden
            style={{
              position: "fixed",
              top: highlight.top - 4,
              left: highlight.left - 4,
              width: highlight.width + 8,
              height: highlight.height + 8,
              borderRadius: 6,
              outline: "2px solid #f2a900",
              background: "rgba(242, 169, 0, 0.08)",
              zIndex: Z.inspectHighlight,
              pointerEvents: "none",
              transition: "top 80ms, left 80ms, width 80ms, height 80ms",
            }}
          />
        )}

        {/* banner */}
        <div
          data-help="help-inspect-banner"
          role="status"
          style={{
            position: "fixed",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: Z.inspectBanner,
            display: "flex",
            alignItems: "center",
            gap: 10,
            maxWidth: "min(560px, calc(100vw - 32px))",
            padding: "6px 8px 6px 16px",
            borderRadius: 20,
            background: "#1f2433",
            color: "#fff",
            boxShadow: tokens.shadow16,
            fontSize: 13,
          }}
        >
          <HandPoint20Regular />
          <span style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {bannerTitle ?? t("help.inspect.banner", "Click any field to read what it does")}
          </span>
          <span style={{ opacity: 0.7, fontSize: 11, whiteSpace: "nowrap" }}>
            {t("help.inspect.escape", "Esc to leave")}
          </span>
          <Button
            size="small"
            appearance="transparent"
            icon={<Dismiss20Regular />}
            aria-label={t("help.inspect.leave", "Leave point-at-a-field mode")}
            onClick={onClose}
            style={{ color: "#fff", minWidth: 0 }}
          />
        </div>

        {miss && (
          <div
            role="status"
            style={{
              position: "fixed",
              top: miss.y + 14,
              left: Math.min(miss.x, window.innerWidth - 260),
              zIndex: Z.inspectCard,
              padding: "6px 10px",
              borderRadius: 6,
              background: "#1f2433",
              color: "#fff",
              fontSize: 12,
              pointerEvents: "none",
            }}
          >
            {t("help.inspect.miss", "Nothing documented here — try the control itself.")}
          </div>
        )}

        {pinned && pinnedText && pos && (
          <div
            ref={cardRef}
            role="dialog"
            aria-label={pinnedText.value.title}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: CARD_WIDTH,
              zIndex: Z.inspectCard,
              padding: 16,
              borderRadius: 12,
              background: tokens.colorNeutralBackground1,
              color: tokens.colorNeutralForeground1,
              boxShadow: tokens.shadow28,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {pinned.step.section && (
                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", color: tokens.colorNeutralForeground3 }}>
                  {localizedSection(page, pinned.step.section, language).value}
                </span>
              )}
              <span style={{ flex: 1 }} />
              {!pinnedText.translated && <EnPill language={language} />}
            </div>
            <div style={{ fontWeight: 600, fontSize: 15, margin: "4px 0 6px" }}>{pinnedText.value.title}</div>
            <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-line", color: tokens.colorNeutralForeground2 }}>
              {pinnedText.value.body}
            </div>
            <div style={{ display: "flex", alignItems: "center", marginTop: 14 }}>
              <Button
                size="small"
                appearance="subtle"
                icon={<Flag20Regular />}
                data-help="help-inspect-continue"
                onClick={() => onContinue(pinned.step)}
              >
                {t("help.inspect.continue", "Continue from here")}
              </Button>
              <span style={{ flex: 1 }} />
              <Button size="small" appearance="primary" onClick={onClose}>
                {t("help.inspect.done", "Done")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Portal>
  );
}
